import { CSSProperties } from 'react';

import styled from 'styled-components';

import { IconBrandJourneys } from '@components/icons/brand-journeys';
import { Menu } from '@components/menu';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { JobTrigger } from './api';
import { useDeleteJobTrigger, useJobTriggers, useUpdateJobTrigger } from './hooks';
import { useJourneySelectorModal } from './journey-selector-modal';
import {
  TRIGGER_MENU_WIDTH,
  TriggerActionMenuItems,
  TriggerUnavailableBadge,
  useRemoveTriggerConfirmation,
} from './trigger-action-menu-items';
import { useTriggerConfigSidebar } from './trigger-config-sidebar';

import { JourneyCard } from '../components/journey-card';
import { useIsAppsAdmin } from '../hooks';

export type StageJourneyIconMenuProps = {
  jobId: number;
  stageId: number;
  stageName: string;
  /** Only one stage per board may render the shared load-failure chip. */
  canReportLoadError?: boolean;
  /**
   * Called once the trigger is gone. Removing unmounts the chip, so the
   * confirmation modal has nothing to hand focus back to — the stage header
   * moves it somewhere stable instead.
   */
  onRemoved?: () => void;
};

/**
 * Green journey chip + popover menu shown in the Kanban stage header when a
 * stage-type trigger exists for that stage. The popover opens with a journey
 * summary card above the shared Edit configuration / Replace / Remove actions.
 *
 * (job, job_stage) is unique for stage triggers, so a stage has at most one
 * matching trigger. Outcome triggers (hired/rejected) never produce this chip.
 *
 * Driven by useJobTriggers(jobId) so it automatically reflects edits made in
 * the triggers sidebar (shared query key, single source of truth).
 */
export const StageJourneyIconMenu = ({
  jobId,
  stageId,
  stageName,
  canReportLoadError = false,
  onRemoved,
}: StageJourneyIconMenuProps) => {
  const { t } = useScopedTranslate(locale);
  const isAdmin = useIsAppsAdmin();
  const triggersQuery = useJobTriggers(jobId);
  const deleteTrigger = useDeleteJobTrigger(jobId);
  const updateTrigger = useUpdateJobTrigger(jobId);
  const confirmRemoveTrigger = useRemoveTriggerConfirmation();
  const showSelectorModal = useJourneySelectorModal();
  const openConfigSidebar = useTriggerConfigSidebar();

  const trigger = (triggersQuery.data ?? []).find(
    (jobTrigger) => jobTrigger.triggerType === 'stage' && jobTrigger.jobStage === stageId
  );

  // At most one stage per job carries a trigger, so reserving space on every
  // stage header would shift the majority of them when the list resolves empty.
  if (triggersQuery.isLoading) {
    return null;
  }

  // The failure is board-wide — the list never arrived, so no stage can know
  // whether it lost a chip. One stage reports it for the whole board.
  if (triggersQuery.isError) {
    if (!canReportLoadError) {
      return null;
    }
    return (
      <Tooltip content={t('loadError')}>
        <ErrorChip
          role="img"
          tabIndex={0}
          aria-label={t('loadError')}
          data-cy="stage-journey-chip-error"
        >
          <MutedJourneysIcon size={16} />
        </ErrorChip>
      </Tooltip>
    );
  }

  if (!trigger) {
    return null;
  }

  const isMutating = updateTrigger.isLoading || deleteTrigger.isLoading;

  const handleEditConfig = () => {
    openConfigSidebar({
      jobId,
      journeyId: trigger.journeyId,
      journeyTitle: trigger.journeyTitle,
    });
  };

  const handleReplace = () => {
    showSelectorModal({
      jobId,
      triggerType: 'stage',
      jobStage: stageId,
      isAdmin,
      onSelect: (journey) => {
        updateTrigger.mutate({
          triggerId: trigger.id,
          journeyId: journey.journeyId,
        });
      },
    });
  };

  const handleRemove = () => {
    confirmRemoveTrigger(trigger, stageName, async () => {
      await deleteTrigger.mutateAsync({ triggerId: trigger.id });
      onRemoved?.();
    });
  };

  return (
    <Menu
      placement="bottom-start"
      width={TRIGGER_MENU_WIDTH}
      buttonProps={{
        variant: 'custom',
        size: 'sm',
        iconLeft: <GreenJourneysIcon size={16} />,
        tooltip: trigger.journeyTitle,
        'aria-label': t('chipLabel', { journeyTitle: trigger.journeyTitle, stageName }),
        dataCy: 'stage-journey-icon-button',
        style: chipStyle,
      }}
    >
      <StageTriggerSummary trigger={trigger} />
      <TriggerActionMenuItems
        trigger={trigger}
        onEditConfig={handleEditConfig}
        onReplace={handleReplace}
        onDelete={handleRemove}
        isMutating={isMutating}
        dataCyPrefix="stage-icon-menu"
      />
    </Menu>
  );
};

type StageTriggerSummaryProps = {
  trigger: JobTrigger;
};

// The trigger already carries description/fieldsCount/preFilledCount from the
// list query — reading them directly here means the popover never has
// to fetch the whole available-journeys catalog just to find one entry in it.
const StageTriggerSummary = ({ trigger }: StageTriggerSummaryProps) => {
  return (
    <SummaryWrapper>
      {!trigger.isAvailable && (
        <UnavailableBadgeWrapper>
          <TriggerUnavailableBadge dataCy="stage-chip-unavailable-badge" />
        </UnavailableBadgeWrapper>
      )}
      <JourneyCard
        title={trigger.journeyTitle}
        description={trigger.description}
        fieldsCount={trigger.fieldsCount}
        preFilledCount={trigger.preFilledCount}
      />
    </SummaryWrapper>
  );
};

// The custom variant's hover rule falls back to the base background var, so an
// explicit hover token is required for any hover feedback at all.
const chipStyle = {
  '--custom-button-bg-color': theme.color.greenTrans10,
  '--custom-button-bg-color-hover': theme.color.greenTrans20,
} as CSSProperties;

// IconBrandJourneys hardcodes its brand fill on each path, so the BaseIcon
// color prop has no effect — override the path fill to the success green.
const GreenJourneysIcon = styled(IconBrandJourneys)({
  path: {
    fill: theme.color.elementGreen200,
  },
});

const MutedJourneysIcon = styled(IconBrandJourneys)({
  path: {
    fill: theme.color.textHelper,
  },
});

const ErrorChip = styled.div({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: pxToRem(32),
  height: pxToRem(32),
  borderRadius: pxToRem(6),
  backgroundColor: theme.color.uiTrans5,
});

const SummaryWrapper = styled.div({
  marginBottom: theme.space[2],
});

const UnavailableBadgeWrapper = styled.div({
  marginBottom: theme.space[2],
});

const locale = {
  en: {
    chipLabel: 'Journey trigger: {journeyTitle} ({stageName})',
    loadError: 'Journey triggers failed to load — refresh the page to try again.',
  },
  is: {
    chipLabel: 'Sjálfvirk ræsing: {journeyTitle} ({stageName})',
    loadError:
      'Ekki tókst að sækja ræsingarreglur fyrir Journey-ferli. Endurhladdu síðunni til að reyna aftur.',
  },
};
